import Alert from '~/common/components/Alerts/Alert';
import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import type { W2CalculationResult } from '~/pages/IncomeCalculator/types';

import BreakdownContainer from '../BreakdownContainer';
import VariableIncomeBreakdown from './VariableIncomeBreakdown';

type YtdIncomeCalculationProps = {
  formula?: string;
  monthlyYearToDate?: number;
};

const YtdIncomeCalculation = ({
  formula,
  monthlyYearToDate,
}: Readonly<YtdIncomeCalculationProps>) => (
  <Box mt={10}>
    <Flex pb={2} alignItems="center">
      <Text mr={1} fontSize="md" display="inline">
        Year-to-Date Income Calculation
      </Text>
    </Flex>

    <Flex pb={2} alignItems="center">
      <Text mr={1} fontSize="md" display="inline">
        Calculation:
      </Text>
      <Text fontSize="md" display="inline" data-testid="ytd-formula">
        {formula}
      </Text>
    </Flex>

    <Flex pb={2} alignItems="center">
      <Text mr={1} fontSize="md" display="inline">
        Calculated Year-to-Date Income:
      </Text>
      <Text fontSize="md" display="inline" data-testid="monthly-ytd-income">
        {formatCurrency(monthlyYearToDate)}
      </Text>
    </Flex>
  </Box>
);

type W2CalculationBreakdownProps = {
  /** The calculation result to view */
  selectedResult: W2CalculationResult;
  /** If calculation has any WVOE calculation  */
  hasWvoeCalculation?: boolean;
  hideAccordion?: boolean;
  onNewWindow?: () => void;
};

const W2CalculationBreakdown = ({
  selectedResult,
  hasWvoeCalculation = false,
  hideAccordion = false,
  onNewWindow,
}: Readonly<W2CalculationBreakdownProps>) => {
  const annualDetails = selectedResult.annualDetails;
  const payType = selectedResult.payType;
  const trimmedPayType = payType.replace('Income', '');

  return (
    <>
      {hasWvoeCalculation && (
        <Flex pb={4} alignItems="center">
          <Alert status="info" mb={4}>
            Picking this option may result in additional documentation to
            validate the stability and amount of income. Underwriter will review
            and condition if needed.
          </Alert>
        </Flex>
      )}
      {/* Salary Base & non-variable Hourly Base */}
      {!selectedResult.isBaseIncomeVariable && (
        <Box mb={6}>
          <BreakdownContainer
            header="Review Base Income Calculation"
            calculatedAmount={selectedResult.calculationAmount}
            onNewWindow={onNewWindow}
            hideAccordion={hideAccordion}
          >
            <Flex pb={2} pt={4} alignItems="center">
              <Text mr={1} fontSize="md" display="inline" fontWeight="bold">
                Calculator:
              </Text>
              <Text
                fontSize="md"
                display="inline"
                data-testid="calculator-type"
              >
                {selectedResult.incomeCalculatorType}
              </Text>
            </Flex>
            <Flex pb={2} alignItems="center">
              <Text mr={1} fontSize="md" display="inline" fontWeight="bold">
                Pay Frequency:
              </Text>
              <Text fontSize="md" display="inline" data-testid="formula-name">
                {trimmedPayType}
              </Text>
            </Flex>
            <Flex pb={12} alignItems="center">
              <Text mr={1} fontSize="md" display="inline" fontWeight="bold">
                Rate:
              </Text>
              <Text fontSize="md" display="inline" data-testid="earning-rate">
                {formatCurrency(selectedResult.earningRate)}
              </Text>
            </Flex>
            <Box pb={2} pt={2}>
              <Flex pb={4} alignItems="center">
                <Text
                  mr={1}
                  display="inline"
                  fontSize="md"
                  fontWeight="bold"
                  textDecoration="underline"
                >
                  Base Income Calculation
                </Text>
              </Flex>

              <Flex pb={4} alignItems="center">
                <Text mr={1} fontSize="md" fontWeight="bold" display="inline">
                  Calculation:
                </Text>
                <Text fontSize="md" display="inline" data-testid="formula">
                  {selectedResult.formula}
                </Text>
              </Flex>

              <Flex pb={2} alignItems="center">
                <Text mr={1} fontSize="md" fontWeight="bold" display="inline">
                  Calculated Monthly Income:
                </Text>
                <Text
                  fontSize="md"
                  display="inline"
                  data-testid="calculation-amount"
                >
                  {formatCurrency(selectedResult.calculationAmount)}
                </Text>
              </Flex>
            </Box>
            {!selectedResult.isYearToDateInstantiated && (
              <YtdIncomeCalculation
                formula={annualDetails[0]?.formula}
                monthlyYearToDate={annualDetails[0]?.monthlyYearToDate}
              />
            )}
          </BreakdownContainer>
        </Box>
      )}

      {/* Variable Income Types*/}
      {selectedResult.variableIncomes
        .filter((variableIncome) => variableIncome.calculatedVariableAmount > 0)
        .map((variableIncome, idx) => (
          <Box
            key={String(variableIncome.calculationField)}
            mt={idx > 0 ? 6 : 0}
            data-testid="additional-variable-incomes"
          >
            <VariableIncomeBreakdown
              {...variableIncome}
              isBaseIncomeVariable={selectedResult.isBaseIncomeVariable}
              hideAccordion={hideAccordion}
            />
          </Box>
        ))}
    </>
  );
};

export default W2CalculationBreakdown;
